import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatNumber, getExchangeLogo } from '../../utils/helpers';
import './TradeTooltip.css';

function TradeTooltip({ trade, livePrice, formatPrice, onClose, onChart, onShare, onDetails, anchorRect }) {
  useEffect(() => {
    if (!trade) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [trade, onClose]);

  if (!trade) return null;

  // Calculate position immediately (not in useEffect) to avoid flash
  const CARD_W = 260;
  const CARD_H = 310; // approximate height
  let style = {};
  if (anchorRect) {
    let left = anchorRect.left + anchorRect.width / 2 - CARD_W / 2;
    let top = anchorRect.top - CARD_H - 8;
    if (top < 8) top = anchorRect.bottom + 8;
    left = Math.max(8, Math.min(left, window.innerWidth - CARD_W - 8));
    top = Math.max(8, Math.min(top, window.innerHeight - CARD_H - 8));
    style = { left: `${left}px`, top: `${top}px` };
  }

  const baseCoin = trade.baseCoin || trade.symbol?.replace(/USDT$|USD$/i, '').toUpperCase() || '';
  // For liquidations the order side is inverted vs the position: a LONG is
  // liquidated by a SELL. Match TradeCard/TradePopup so the tooltip never
  // shows the opposite direction from the card it belongs to.
  const isLong = trade.isLiquidation ? trade.side === 'sell' : trade.side === 'buy';
  const sideLabel = isLong ? 'LONG' : 'SHORT';
  const sideClass = isLong ? 'tt-long' : 'tt-short';
  const exchangeName = trade.exchange?.charAt(0) + trade.exchange?.slice(1).toLowerCase();
  const formattedLive = livePrice ? (formatPrice ? formatPrice(livePrice) : `$${formatNumber(livePrice)}`) : '—';
  const time = new Date(trade.timestamp);
  const timeStr = time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  return createPortal(
    <>
      <div className="tt-overlay" onClick={onClose} />
      <div className="tt-card" style={style} onClick={(e) => e.stopPropagation()}>
        {/* Glow accent */}
        <div className={`tt-glow ${sideClass}`} />

        {/* Header */}
        <div className="tt-header">
          <div className="tt-header-left">
            {trade.logoUrl && <img className="tt-coin-icon" src={trade.logoUrl} alt={baseCoin} />}
            <span className="tt-symbol">{baseCoin}</span>
            <span className={`tt-side-badge ${sideClass}`}>{sideLabel}</span>
          </div>
          <button className="tt-close" onClick={onClose}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M1 1l8 8M9 1l-8 8"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="tt-body">
          <div className="tt-row">
            <span className="tt-label">Exchange</span>
            <span className="tt-value">
              <img className="tt-exch-icon" src={trade.exchangeLogo || getExchangeLogo(trade.exchange)} alt={trade.exchange} />
              {exchangeName}
            </span>
          </div>
          <div className="tt-divider" />
          <div className="tt-row">
            <span className="tt-label">{trade.isLiquidation ? 'Liq Price' : 'Entry Price'}</span>
            <span className="tt-value">${formatNumber(trade.price)}</span>
          </div>
          <div className="tt-row">
            <span className="tt-label">Live Price</span>
            <span className="tt-value tt-live">{formattedLive}</span>
          </div>
          <div className="tt-divider" />
          <div className="tt-row">
            <span className="tt-label">Volume</span>
            <span className={`tt-value tt-volume ${sideClass}`}>${formatNumber(trade.dollarSize)}</span>
          </div>
          <div className="tt-row">
            <span className="tt-label">Time</span>
            <span className="tt-value">{timeStr}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="tt-actions">
          <button className="tt-action" onClick={() => onChart(baseCoin)} title="Live Chart">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M5 3v16h16v2H3V3h2zm14.94 2.94l2.12 2.12L16 14.12l-3-3-4.94 4.94-2.12-2.12L13 6.88l3 3 4.94-4.94z"/></svg>
            <span>Chart</span>
          </button>
          <button className="tt-action" onClick={() => onShare(trade, livePrice)} title="Share">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
            <span>Share</span>
          </button>
          <button className="tt-action" onClick={() => onDetails(baseCoin)} title="Coin Details">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
            <span>Details</span>
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

export default TradeTooltip;
